import { XUIPanelSection } from '@xero/xui/react/panel';
import { FC, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { IShopifySettingsResponse, IAccountAndTaxMapping } from '../../../../models';
import { useAppDispatch } from '../../../../redux/store';
import { TaxMappingTable } from '../../../../components/TaxMappingTable';
import { getSettingsAction, settingsSelectors } from '../../../../redux/slices/SettingsSlice';
import { ShopifyLang } from '../../../../static/lang/en-NZ/shopify';
import './AccountAndTaxSetupTab.css';
import TaxDisclaimer from '../../../../components/TaxDisclaimer';
import CheckBox from '../../../../components/CheckBox';
import { isCommonwealthRegion } from '../../../../../src/helpers/region';

export interface IAccountAndTaxSetupTabProps {
    integrationLang: ShopifyLang;
    onValid: (isValid: boolean) => void;
    isValid: boolean;
    onSetAccountAndTaxMapping: (taxMappings: IAccountAndTaxMapping[]) => void;
}

export const AccountAndTaxSetupTab: FC<IAccountAndTaxSetupTabProps> = ({
    integrationLang,
    onValid,
    isValid,
    onSetAccountAndTaxMapping,
}) => {
    const dispatch = useAppDispatch();
    const settings = useSelector(settingsSelectors.settings) as IShopifySettingsResponse;
    const lang = integrationLang.SETUP_PAGE.ACCOUNT_AND_TAX_SETUP_TAB;
    const [checkBoxChecked, setCheckBoxChecked] = useState(isValid);
    const [mappingValid, setMappingValid] = useState(isValid);
    const isCommonwealth = isCommonwealthRegion(settings.xeroRegion);
    const checkInitSettingsRef = useRef(false);

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            setCheckBoxChecked(!checkBoxChecked);
        }
    };

    useEffect(() => {
        const shouldDispatchSettingsGet = !checkInitSettingsRef.current && !settings.installationId;

        if (shouldDispatchSettingsGet) {
            dispatch(getSettingsAction());
        }

        checkInitSettingsRef.current = true;
    }, [dispatch, settings.installationId]);

    useEffect(() => {
        if (checkBoxChecked && mappingValid) {
            onValid(true);
        } else {
            onValid(false);
        }
        // All dependencies used in a hook should be injected here.
        // This is to ensure that the application reacts to all state and state does not get out of sync.
        // When working in this hook please take the time to refactor, remove this comment and resolve the dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [checkBoxChecked]);

    return (
        <XUIPanelSection className='shopify-panel--section xui-padding-bottom-2xlarge' heading={lang.TITLE}>
            <div className='content shopify-content'>
                <div className='shopify-panel--description xui-padding-top-large'>
                    <div className='xui-padding-bottom-xlarge'>{lang.ACCOUNT_AND_TAX_SETUP_DETAILS}</div>
                    <div className='xui-padding-bottom-3xlarge'>
                        {
                            /*Tax region title*/
                            settings.paysTax && isCommonwealth ? (
                                <div className='xui-padding-bottom-large'>
                                    <div className='xui-text-label xui-fieldlabel-layout'>
                                        {lang.PRIMARY_TAX_REGION_TITLE}
                                    </div>
                                    <div>{lang.PRIMARY_TAX_REGION[settings?.xeroRegion || '']}</div>
                                </div>
                            ) : (
                                <div />
                            )
                        }
                        {
                            /*Tax blurb*/
                            isCommonwealth ? (
                                <TaxDisclaimer
                                    blurb={settings.paysTax ? lang.PRIMARY_BLURB : lang.GLOBAL_BLURB}
                                    showIcon={true}
                                />
                            ) : (
                                <div />
                            )
                        }
                    </div>
                </div>
            </div>
            <TaxMappingTable
                hideRegionColumn={!isCommonwealth}
                allChartOfAccounts={settings.chartOfAccounts}
                accountAndTaxMappings={settings.accountAndTaxMappings}
                allTaxRates={settings.xeroTaxRates}
                onSetAccountAndTaxMapping={onSetAccountAndTaxMapping}
                setMappingValid={setMappingValid}
                setCheckBoxChecked={setCheckBoxChecked}
            />
            <div className='confirmation-content xui-padding-top-2xlarge' onKeyDown={handleKeyDown}>
                <CheckBox
                    text={lang.CONFIRMATION_BLURB}
                    checkBoxChecked={checkBoxChecked}
                    onCheckBoxChange={setCheckBoxChecked}
                />
            </div>
        </XUIPanelSection>
    );
};
